import { useNavigate, useParams, unstable_usePrompt } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Card, Col, Form, Row } from "react-bootstrap";
import IContact, { IContactCreateOrUpdate, IContactInitResult } from "models/sql/IContact";
import ContactService from "services/ContactService";
import MarkdownEditor from "components/MarkdownEditor";
import { convertToDateTimeLocalString, formatDate } from "utilities/StringUtility";
import moment from "moment";
import { useBeforeUnload } from "utilities/UIUtilities";
import DetailFooter from "components/DetailFooter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faComments, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { generateLog } from "utilities/MiscUtilities";

interface IContactsDetailProps {
    baseData?: IContactInitResult;
}

const ContactsDetail = (props: IContactsDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const { id }: any = useParams();
    const [contact, setContact] = useState<IContact | null>({
        contactId: -1,
        firstName: undefined,
        lastName: undefined,
        email: undefined,
        phone: undefined,
        role: undefined,
        created: undefined,
        notes: undefined,
        systemDataJson: undefined,
        lastContactDate: undefined,
        lastUpdated: undefined,
        lastUpdatedBy: undefined,
        type: undefined,
        source: undefined
    });
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);
    const dealerRoles = ["Sales Rep", "Sales Manager", "Sales Director/VP", "Purchasing", "Owner/President", "Other"];
    const fireDepartmentRoles = ["BN Chief", "Chief", "Lieutenant", "Health & Safety", "Purchasing", "Other"];
    const roles = Array.from(new Set([...dealerRoles, ...fireDepartmentRoles]));
    const types = ["End Customer", "Dealer Rep", "Supplier/ Contractor"];

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page.");

    useEffect(
        () => {
            (async () => {
                if (id !== undefined) {
                    globalContext.setLoading(true);

                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await ContactService.GetAsync(accessToken, id);

                    if (result.record) {
                        setContact(result.record);
                    }
                    else {
                        setContact(null);
                        globalContext.showErrorMessage("Unable to load Contact");
                    }

                    globalContext.setLoading(false);
                }
            })();
        }, [id]);

    const deleteAsync = async () => {
        if (contact && contact.contactId && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await ContactService.DeleteAsync(accessToken, contact.contactId);
            globalContext.setLoading(false);
            navigate("/Contacts");
        }
    };

    const submitFormAsync = async () => {
        if (contact) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: IContactCreateOrUpdate = {
                contactId: contact.contactId,
                firstName: contact.firstName,
                lastName: contact.lastName,
                email: contact.email,
                phone: contact.phone,
                role: contact.role,
                created: contact.created,
                notes: contact.notes,
                systemDataJson: contact.systemDataJson,
                lastContactDate: contact.lastContactDate,
                lastUpdated: contact.lastUpdated,
                lastUpdatedBy: contact.lastUpdatedBy,
                type: contact.type,
                source: contact.source
            };

            await ContactService.CreateOrUpdateAsync(accessToken, data);
            globalContext.setLoading(false);
            setHasFormDataChanged(false);

            if (data.contactId === -1) {
                navigate(-1);
            }
        }
    };

    if (contact) {
        return (
            <>
                <Row>
                    <Col>
                        <Card className="mb-2 mt-2">
                            <Card.Body>
                                <Card.Title>
                                    {
                                        id !== undefined &&
                                        <strong>{contact.firstName} {contact.lastName}</strong>
                                    }
                                    {
                                        id === undefined &&
                                        <strong>New Contact</strong>
                                    }
                                    {
                                        contact.fireDepartmentContactLinks && contact.fireDepartmentContactLinks.length > 0 &&
                                        contact.fireDepartmentContactLinks.map(
                                            (fdcl, index) =>
                                                <><br /><FontAwesomeIcon icon={faUpRightFromSquare as IconProp} />
                                                    &nbsp;<a className="ps-2" href={`/Departments/Detail/${fdcl.fireDepartment.fireDepartmentId}`} target="_blank">Department: {fdcl.fireDepartment.fireDeptName}</a></>
                                        )
                                    }
                                    {
                                        contact.dealerContactLinks && contact.dealerContactLinks.length > 0 &&
                                        contact.dealerContactLinks.map(
                                            (dcl, index) =>
                                                <><br /><FontAwesomeIcon icon={faUpRightFromSquare as IconProp} />
                                                    &nbsp;<a className="ps-2" href={`/Dealers/Detail/${dcl.dealer.dealerName}`} target="_blank">Dealer: {dcl.dealer.dealerName}</a></>
                                        )
                                    }
                                </Card.Title >
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>First Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setContact({ ...contact, firstName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={contact.firstName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Last Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setContact({ ...contact, lastName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={contact.lastName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label><a href={`MailTo:${contact.email}`} target="_blank"><FontAwesomeIcon icon={faUpRightFromSquare as IconProp} /> Email</a></Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setContact({ ...contact, email: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={contact.email}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label><a href={`Tel:${contact.phone}`} target="_blank"><FontAwesomeIcon icon={faUpRightFromSquare as IconProp} /> Phone</a></Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setContact({ ...contact, phone: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={contact.phone}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Role</Form.Label>
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setContact({ ...contact, role: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={contact.role}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    roles.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Type</Form.Label>
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setContact({ ...contact, type: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={contact.type}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    types.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Source</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setContact({ ...contact, source: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={contact.source}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Last Contacted</Form.Label>
                                            {
                                                <Form.Control
                                                    type="datetime-local"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setContact({ ...contact, lastContactDate: new Date(e.target.value).toISOString() });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={contact.lastContactDate ? convertToDateTimeLocalString(contact.lastContactDate) : undefined}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Admin Notes</Form.Label>
                                            <MarkdownEditor
                                                noun="Note"
                                                onChange={newVal => {
                                                    setContact({ ...contact, notes: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={contact.notes || ""}
                                            />
                                        </Form.Group>
                                    </Row>
                                }

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Last Updated</Form.Label>
                                                {
                                                    <Form.Control
                                                        type="date"
                                                        readOnly
                                                        value={contact.lastUpdated ? moment(contact.lastUpdated).format('yyyy-MM-DD') : undefined}
                                                    />
                                                }
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Last Updated By</Form.Label>
                                                {
                                                    <Form.Control
                                                        type="text"
                                                        readOnly
                                                        value={contact.lastUpdatedBy}
                                                    />
                                                }
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <Row className="align-items-end">
                        <Col>
                            <Card className="mb-2">
                                <Card.Body>
                                    <Card.Title>
                                        <strong>Log</strong><br />
                                    </Card.Title>
                                    {generateLog<IContact>(contact)}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                }

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }

            </>
        );
    }
    else {
        return (
            <>
                <h1>There was an issue retreiving the Contact, please try again.</h1>
            </>
        );
    }
}

export default ContactsDetail;
